import { useState, useEffect, useCallback } from "react";
import { C } from "./config.js";
import { loadLoginParse } from "./api.js";
import { GameAdmin } from "./GameAdmin.jsx";
import "./adm.css";

/* ================================================================
   세션 유지 시간 설정 (분)
   ================================================================ */
const SESSION_TIMEOUT_MIN = 120; // 2시간 후 자동 로그아웃

function LoginScreen({ onLogin }) {
  const [id, setId] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [shake, setShake] = useState(false);

  const go = async () => {
    if (!id || !pw) {
      setErr("아이디와 비밀번호를 입력하세요.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const Parse = await loadLoginParse();
      const user = await Parse.User.logIn(id, pw);
      // 로그인 시간 저장
      localStorage.setItem("adm_login_at", Date.now().toString());
      onLogin(user);
    } catch (e) {
      setErr(
        e.code === 101
          ? "아이디 또는 비밀번호가 올바르지 않습니다."
          : `오류: ${e.message}`,
      );
      setShake(true);
      setTimeout(() => setShake(false), 500);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: C.bg,
        fontFamily: "'Pretendard','Noto Sans KR',-apple-system,sans-serif",
      }}
    >
      <div
        style={{
          background: C.surface,
          borderRadius: 18,
          width: 380,
          padding: "40px 36px",
          border: `1px solid ${C.border}`,
          boxShadow: "0 8px 40px rgba(92,124,206,.15)",
          animation: shake ? "adm-shake .4s ease" : "adm-fadeUp .4s ease",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 14,
              background: C.accentBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 14px",
              fontSize: 24,
            }}
          >
            🎮
          </div>
          <div
            style={{
              fontSize: 20,
              fontWeight: 800,
              color: C.t1,
              letterSpacing: "-.5px",
            }}
          >
            <em style={{ color: C.accent, fontStyle: "normal" }}>Game</em>{" "}
            관리자
          </div>
          <div style={{ fontSize: 13, color: C.t3, marginTop: 6 }}>
            관리자 계정으로 로그인하세요
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: C.t2,
              display: "block",
              marginBottom: 6,
            }}
          >
            아이디
          </label>
          <input
            type="text"
            value={id}
            placeholder="아이디를 입력하세요"
            onChange={(e) => {
              setId(e.target.value);
              setErr("");
            }}
            onKeyDown={(e) => e.key === "Enter" && go()}
            style={{
              width: "100%",
              padding: "11px 14px",
              border: `1px solid ${C.border2}`,
              borderRadius: 10,
              fontSize: 14,
              color: C.t1,
              background: C.bg,
              outline: "none",
              boxSizing: "border-box",
              fontFamily: "inherit",
              transition: "border .15s",
            }}
            onFocus={(e) => (e.target.style.borderColor = C.accent)}
            onBlur={(e) => (e.target.style.borderColor = C.border2)}
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: C.t2,
              display: "block",
              marginBottom: 6,
            }}
          >
            비밀번호
          </label>
          <input
            type="password"
            value={pw}
            placeholder="비밀번호를 입력하세요"
            onChange={(e) => {
              setPw(e.target.value);
              setErr("");
            }}
            onKeyDown={(e) => e.key === "Enter" && go()}
            style={{
              width: "100%",
              padding: "11px 14px",
              border: `1px solid ${C.border2}`,
              borderRadius: 10,
              fontSize: 14,
              color: C.t1,
              background: C.bg,
              outline: "none",
              boxSizing: "border-box",
              fontFamily: "inherit",
              transition: "border .15s",
            }}
            onFocus={(e) => (e.target.style.borderColor = C.accent)}
            onBlur={(e) => (e.target.style.borderColor = C.border2)}
          />
        </div>

        {err && (
          <div
            style={{
              fontSize: 13,
              color: C.danger,
              marginBottom: 14,
              padding: "10px 12px",
              background: C.dangerBg,
              borderRadius: 8,
              border: "1px solid #fdc5ce",
            }}
          >
            {err}
          </div>
        )}

        <button
          onClick={go}
          disabled={busy}
          style={{
            width: "100%",
            padding: "13px",
            border: "none",
            borderRadius: 10,
            background: busy ? C.border2 : C.accent,
            color: "#fff",
            fontSize: 14,
            fontWeight: 700,
            cursor: busy ? "not-allowed" : "pointer",
            fontFamily: "inherit",
            transition: "all .15s",
            boxShadow: busy ? "none" : "0 4px 14px rgba(92,124,206,.35)",
          }}
          onMouseEnter={(e) => {
            if (!busy) e.currentTarget.style.background = C.accentDim;
          }}
          onMouseLeave={(e) => {
            if (!busy) e.currentTarget.style.background = C.accent;
          }}
        >
          {busy ? "로그인 중…" : "로그인"}
        </button>
      </div>

      <style>{`
        @keyframes adm-fadeUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
        @keyframes adm-shake  { 0%,100%{transform:translateX(0)} 20%,60%{transform:translateX(-8px)} 40%,80%{transform:translateX(8px)} }
      `}</style>
    </div>
  );
}

/* ================================================================
   App
   ================================================================ */
export default function App() {
  const [step, setStep] = useState("init");
  const [user, setUser] = useState(null);

  const doLogout = useCallback(async (silent = false) => {
    try {
      const Parse = await loadLoginParse();
      await Parse.User.logOut();
    } catch {}
    localStorage.removeItem("adm_login_at");
    setUser(null);
    setStep("login");
    if (!silent) alert("세션이 만료되었습니다. 다시 로그인해주세요.");
  }, []);

  // 초기화 + 세션 유효성 확인
  useEffect(() => {
    loadLoginParse().then((Parse) => {
      const cur = Parse.User.current();
      if (!cur) {
        setStep("login");
        return;
      }

      // 세션 만료 확인
      const loginAt = Number(localStorage.getItem("adm_login_at") || 0);
      const elapsed = (Date.now() - loginAt) / 1000 / 60; // 분
      if (loginAt && elapsed > SESSION_TIMEOUT_MIN) {
        doLogout(true);
        return;
      }

      setUser(cur);
      setStep("dashboard");
    });
  }, [doLogout]);

  // 세션 만료 확인 함수
  const checkSession = useCallback(() => {
    const loginAt = Number(localStorage.getItem("adm_login_at") || 0);
    if (!loginAt) return;
    const elapsed = (Date.now() - loginAt) / 1000 / 60;
    if (elapsed > SESSION_TIMEOUT_MIN) doLogout();
  }, [doLogout]);

  // 탭 포커스 시 세션 확인
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") checkSession();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [checkSession]);

  const handleLogin = (u) => {
    setUser(u);
    setStep("dashboard");
  };
  const handleLogout = () => doLogout(true);

  if (step === "init")
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: C.bg,
          fontSize: 14,
          color: C.t3,
        }}
      >
        초기화 중…
      </div>
    );

  if (step === "login") return <LoginScreen onLogin={handleLogin} />;

  return <GameAdmin user={user} onLogout={handleLogout} />;
}
